<div class="finance-list-page">
    <app-page-shell>
        <p-toast position="top-center"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero eyebrow="Payables" title="Credit Notes"
            subtitle="Track vendor credits, source bills, allocation status, lifecycle state, and payable reductions.">
            <div actions>
                <p-button *ngIf="canAddCreditNote && !isConsolidatedReadOnly" label="Add Credit Note" icon="fa-solid fa-plus"
                    (click)="addCreditNote()"></p-button>
            </div>
        </app-page-hero>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <app-page-search-panel title="Search credit notes" [activeFilters]="activeFilterCount"
            resultText="Filter by company scope and review matching vendor credits." [collapsed]="true"
            (search)="applyFilters()" (clear)="clearFilters()">
            <div class="finance-search-grid">
                <app-company-scope-selector (scopeChange)="onScopeChange($event)"></app-company-scope-selector>
            </div>
        </app-page-search-panel>

        <app-page-data-panel title="Credit note directory"
            subtitle="CRM vendor, original bill, amount, status, and line preview in one finance table."
            [resultText]="resultText">
            <app-accounting-directory-controls documentType="CREDIT_NOTE" [filters]="directoryFilters"
                [searchText]="searchText" [selectedStatuses]="selectedDocumentStatuses" [pendingStatuses]="pendingStatuses" (searchTextChange)="onDirectorySearch($event)"
                (modeChange)="onDirectoryModeChange($event)" (pending)="showPendingStatuses()"
                (all)="showAllStatuses()"></app-accounting-directory-controls>
            <p-table *ngIf="directoryMode === 'LIST'" class="finance-table" [value]="creditNotes" dataKey="creditNoteID" [paginator]="true"
                [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords"
                [lazy]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                (onLazyLoad)="loadData($event)" [rowsPerPageOptions]="[20, 50, 100]"
                [tableStyle]="{ 'min-width': '0' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Credit Note</th>
                        <th>Vendor/Original Bill</th>
                        <th>Amounts/Tax</th>
                        <th>Workflow/Allocation Status</th>
                        <th>Reason/Dates</th>
                        <th>Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-creditNote>
                    <tr>
                        <td data-label="Credit Note">
                            <div class="stack-cell">
                                <span class="cell-title">{{ creditNote.creditNoteNumber || 'No credit note number' }}</span>
                                <div class="cell-meta">
                                    <span class="code-pill">ID {{ creditNote.creditNoteID || '-' }}</span>
                                    <span>{{ creditNote.company?.name || '-' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Vendor/Original Bill">
                            <div class="stack-cell">
                                <span class="cell-title">{{ creditNote.crmDisplayName || creditNote.crmClient?.companyName || creditNote.customer?.name || '-' }}</span>
                                <div class="cell-meta vertical">
                                    <span *ngIf="creditNote.crmContactDisplayName || creditNote.crmContactEmail || creditNote.crmContactPhone">
                                        {{ creditNote.crmContactDisplayName || creditNote.crmContactEmail || creditNote.crmContactPhone }}
                                    </span>
                                    <span>Bill {{ creditNote.originalBillNumber || creditNote.bill?.billNumber || '-' }}</span>
                                    <span>{{ creditNote.crmAddress || 'Address pending' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Amounts/Tax">
                            <div class="amount-block">
                                <span class="amount-primary" *ngIf="creditNote.taxAmount != null">{{ formatKnownMoney(getGrossAmount(creditNote)) }}</span>
                                <span class="amount-primary" *ngIf="creditNote.taxAmount == null">{{ 'accountingAmounts.beforeTax' | transloco }} {{ formatKnownMoney(getBeforeTaxAmount(creditNote)) }}</span>
                                <span class="amount-secondary" *ngIf="creditNote.taxAmount != null">{{ 'accountingAmounts.beforeTax' | transloco }} {{ formatKnownMoney(getBeforeTaxAmount(creditNote)) }}</span>
                                <span class="amount-secondary">Tax {{ formatKnownMoney(creditNote.taxAmount ?? null) }}</span>
                                <span class="amount-secondary">{{ creditNote.creditNoteLines?.length || 0 }} lines</span>
                            </div>
                        </td>
                        <td data-label="Workflow/Allocation Status">
                            <div class="status-stack">
                                <p-tag [severity]="getStatusSeverity(getDocumentStatus(creditNote))"
                                    [value]="getDocumentStatus(creditNote)"></p-tag>
                                <span class="muted-pill">{{ getPostingStatus(creditNote) }}</span>
                                <span class="muted-pill">{{ creditNote.allocationStatus || 'UNAPPLIED' }}</span>
                            </div>
                        </td>
                        <td data-label="Reason/Dates">
                            <div class="stack-cell">
                                <span class="cell-title">{{ creditNote.reason || 'No reason' }}</span>
                                <div class="cell-meta vertical">
                                    <span>Note date {{ creditNote.creditNoteDate | date:'yyyy-MM-dd' }}</span>
                                    <span>Created {{ creditNote.createdAt | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Actions">
                            <div class="row-actions">
                                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" pTooltip="View credit note"
                                    (click)="viewCreditNote(creditNote)"></p-button>
                                <p-button icon="pi pi-list" [rounded]="true" [text]="true" pTooltip="Preview lines"
                                    (click)="openPreview(creditNote)"></p-button>
                                <p-button *ngIf="canEditCreditNote && !isConsolidatedReadOnly" icon="pi pi-pencil" [rounded]="true" [text]="true"
                                    severity="info" pTooltip="Edit Credit Note" [disabled]="!isEditable(creditNote)"
                                    (click)="editCreditNote(creditNote)"></p-button>
                                <app-finance-pdf-view-button kind="credit-note" [documentId]="creditNote.creditNoteID" [documentReference]="creditNote.creditNoteNumber" [postingStatus]="creditNote.postingStatus" [iconOnly]="true" [disabled]="documentActions.isPending(creditNote.creditNoteID)"></app-finance-pdf-view-button>
                                <p-button icon="pi pi-download" [rounded]="true" [text]="true" severity="help" [loading]="documentActions.isPending(creditNote.creditNoteID)"
                                    pTooltip="Download Credit Note" (click)="downloadCreditNote(creditNote)"></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="6">
                            <div class="empty-state">
                                <i class="pi pi-inbox"></i>
                                <strong>No credit notes found</strong>
                                <span>No credit notes are available for the current company.</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </app-page-shell>

    <p-drawer [(visible)]="isPreviewVisible" position="right" styleClass="finance-preview-drawer">
        <ng-template pTemplate="header">
            <div class="stack-cell">
                <strong>{{ previewCreditNote?.creditNoteNumber || 'Credit note lines' }}</strong>
                <span class="cell-meta">{{ previewCreditNote?.creditNoteLines?.length || 0 }} lines</span>
            </div>
        </ng-template>
        <div class="preview-list" *ngIf="previewCreditNote?.creditNoteLines?.length; else noCreditNoteLines">
            <div class="preview-line" *ngFor="let line of previewCreditNote?.creditNoteLines">
                <div class="preview-line-header">
                    <div>
                        <div class="preview-line-title">{{ line.description || 'Line item' }}</div>
                    </div>
                    <div class="preview-line-amount">{{ formatMoney(line.amount, 'SAR') }}</div>
                </div>
            </div>
        </div>
        <ng-template #noCreditNoteLines>
            <div class="preview-empty">No credit note lines available.</div>
        </ng-template>
    </p-drawer>
</div>
